/**
 * Unterscheidet einen Request, der durch das Entladen des Dokuments stirbt,
 * von einem echten Netzwerkfehler.
 *
 * ## Warum das nötig ist
 *
 * Chromium bricht beim Commit einer Navigation ALLE noch offenen Requests des
 * alten Dokuments mit `net::ERR_ABORTED` ab. In JavaScript erscheint das als
 * `TypeError: Failed to fetch` — am Fehlerobjekt allein ist es von einem
 * echten Netzwerkfehler NICHT unterscheidbar. Ein `AbortController` hilft
 * hier nicht: Beim Entladen wird nichts von der App abgebrochen, und ein
 * React-Unmount findet auch nicht statt — das Dokument wird schlicht
 * verworfen.
 *
 * ## Das Unterscheidungsmerkmal
 *
 * Gemessen in Chromium (DI-7, `page.goto()` mit hängendem Request):
 *
 *   beforeunload → pagehide → visibilitychange(hidden) → unload
 *   → Ablehnung des fetch-Promise
 *
 * `pagehide` ist also bereits gefeuert, wenn der `catch`-Block läuft. Ein auf
 * `pagehide` gesetztes Flag ist damit ein zuverlässiges, kein heuristisches
 * Merkmal.
 *
 * Bewusst NICHT verwendet: `document.visibilityState === 'hidden'`. Das ist
 * auch wahr, wenn der Tab nur in den Hintergrund wechselt — damit würden
 * echte Netzwerkfehler in Hintergrund-Tabs verschluckt.
 *
 * ## Grenze
 *
 * Das Flag ist ausschliesslich während des Entladens gesetzt. Ein echter
 * Netzwerkfehler im lebenden Dokument bleibt sichtbar und wird weiterhin
 * geloggt — die Unterdrückung darf keinen blinden Fleck erzeugen.
 */

let documentIsUnloading = false;

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    documentIsUnloading = true;
  });
  // Rückkehr aus dem bfcache: Das Dokument lebt weiter, also muss ab hier
  // wieder jeder Fehler sichtbar sein.
  window.addEventListener('pageshow', () => {
    documentIsUnloading = false;
  });
}

/** True, solange das Dokument gerade entladen wird (nach `pagehide`). */
export function isDocumentUnloading(): boolean {
  return documentIsUnloading;
}

/**
 * True nur dann, wenn der Fehler ein Netzwerk-Abbruch ist UND das Dokument
 * gerade entladen wird. In jedem anderen Fall — insbesondere bei jedem
 * echten Netzwerkfehler — false, damit der Aufrufer normal loggt.
 */
export function isRequestSupersededByUnload(error: unknown): boolean {
  if (!documentIsUnloading) {
    return false;
  }
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  // `fetch` lehnt Netzwerkfehler grundsätzlich mit einem TypeError ab
  // (Chromium: "Failed to fetch", Firefox/Safari mit anderem Text) — deshalb
  // wird auf den Typ geprüft, nicht auf den Text.
  return error instanceof TypeError;
}
